import { useTranslation } from 'next-i18next'
import { useRouter } from 'next/router'
import React from 'react'

import { formatLocalPrice } from '../../functions/localeCurrency'
import s from './landing.module.css'

/** Descoberta geolocalizada: features numeradas + pilha de cards do deck. */
export function Discovery() {
  const { t } = useTranslation('landing')

  const features: Array<[string, string, string]> = [
    ['01', t('discovery.f1Title'), t('discovery.f1Copy')],
    ['02', t('discovery.f2Title'), t('discovery.f2Copy')],
    ['03', t('discovery.f3Title'), t('discovery.f3Copy')],
  ]

  return (
    <section className={s.sec} id="descoberta">
      <p className={`${s.kick} text-brand`}>
        <span className={s.kbar} />
        {t('discovery.kicker')}
      </p>
      <h2 className={`${s.d} ${s.h2} text-ink`} style={{ margin: '22px 0 0' }}>
        {t('discovery.title1')}{' '}
        <span className={s.outR}>{t('discovery.title2')}</span>
      </h2>
      <div
        style={{
          display: 'flex',
          gap: 24,
          flexWrap: 'wrap',
          marginTop: 16,
          justifyContent: 'space-between',
          alignItems: 'flex-start',
        }}
      >
        <div style={{ maxWidth: 520 }}>
          <p className={s.lead}>{t('discovery.lead')}</p>
          <div style={{ display: 'grid', gap: 34, marginTop: 44 }}>
            {features.map(([num, title, copy]) => (
              <div className={s.feat} key={num}>
                <div className={s.fnum}>{num}</div>
                <div className={s.ft}>{title}</div>
                <p className={s.fp}>{copy}</p>
              </div>
            ))}
          </div>
        </div>

        <DeckMock />
      </div>
    </section>
  )
}

/** Pilha de cards do deck com ranking sobreposto (mock ilustrativo). */
function DeckMock() {
  const { t } = useTranslation('landing')

  return (
    <div
      style={{
        position: 'relative',
        paddingBlock: '30px 70px',
        paddingInline: '30px 40px',
        minWidth: 320,
      }}
      role="img"
      aria-label={t('discovery.deckAria')}
    >
      <div
        className={s.deckghost}
        style={{ transform: 'rotate(8deg)', insetInlineEnd: 12, top: 16 }}
      />
      <div
        className={s.deckghost}
        style={{ transform: 'rotate(4deg)', insetInlineEnd: 22, top: 22 }}
      />
      <div className={s.deckcard} style={{ transform: 'rotate(-2deg)' }}>
        <div
          className="bg-section-black"
          style={{
            height: 210,
            borderRadius: 14,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <span className={s.d} style={{ fontSize: 64, color: '#fff' }}>
            BS
          </span>
        </div>
        <div style={{ fontWeight: 700, fontSize: 16, marginTop: 12 }}>
          {t('discovery.deckName')}
        </div>
        <div style={{ fontSize: 13, color: '#7b7b7b' }}>
          {t('discovery.deckMeta')}
        </div>
        <div style={{ display: 'flex', gap: 8, marginTop: 12 }}>
          <div className={`${s.sbtn} ${s.sbo}`} style={{ flex: 0.5 }}>
            ✕
          </div>
          <div className={`${s.sbtn} ${s.sbr}`}>{t('discovery.deckTrain')}</div>
          <div className={`${s.sbtn} ${s.sbo}`}>
            {t('discovery.deckChallenge')}
          </div>
        </div>
      </div>
      <div
        className={s.deckcard}
        style={{
          position: 'absolute',
          width: 230,
          padding: 16,
          insetInlineEnd: -6,
          bottom: -10,
          transform: 'rotate(3deg)',
        }}
      >
        <div
          className={`${s.cond} text-brand`}
          style={{ fontWeight: 700, fontSize: 13, letterSpacing: '.16em' }}
        >
          {t('discovery.rankingTitle')}
        </div>
        {[
          ['1 · Ediane R.', '412 OSS'],
          ['2 · Carlão M.', '387 OSS'],
          [t('discovery.rankingYou'), '—'],
        ].map(([name, pts], i) => (
          <div
            key={name}
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              fontSize: 14,
              marginTop: i === 0 ? 8 : 5,
            }}
          >
            <span style={{ fontWeight: 600 }}>{name}</span>
            <span className="text-brand" style={{ fontWeight: 700 }}>
              {pts}
            </span>
          </div>
        ))}
      </div>
    </div>
  )
}

/** Split Treinar junto (vermelho) / Desafiar (branco) — o core loop. */
export function CoreLoop() {
  const { t } = useTranslation('landing')

  return (
    <section id="desafiar" style={{ display: 'flex', flexWrap: 'wrap' }}>
      <div
        className="bg-brand"
        style={{ flex: 1, minWidth: 320, padding: '90px 7vw' }}
      >
        <p className={`${s.kick} text-white`}>
          <span className={s.kbar} />
          {t('coreloop.trainKicker')}
        </p>
        <h3
          className={`${s.d} text-white`}
          style={{ fontSize: 'clamp(52px,5.4vw,86px)', margin: '20px 0 14px' }}
        >
          {t('coreloop.trainTitle1')}
          <br />
          {t('coreloop.trainTitle2')}
        </h3>
        <p
          className={s.lead}
          style={{
            fontSize: 17,
            maxWidth: 420,
            color: 'rgba(255,255,255,.85)',
          }}
        >
          {t('coreloop.trainLead')}
        </p>
        <div
          className="text-white"
          style={{
            display: 'flex',
            flexDirection: 'column',
            gap: 10,
            marginTop: 16,
          }}
        >
          {[1, 2, 3].map((n) => (
            <div className={s.tickrow} key={n}>
              <span className={s.tick}>✓</span>
              {t(`coreloop.trainTick${n}`)}
            </div>
          ))}
        </div>
      </div>

      <div
        style={{
          flex: 1,
          minWidth: 320,
          padding: '90px 7vw',
          background: '#fff',
        }}
      >
        <p className={`${s.kick} text-brand`}>
          <span className={s.kbar} />
          {t('coreloop.challengeKicker')}
        </p>
        <h3
          className={s.d}
          style={{ fontSize: 'clamp(52px,5.4vw,86px)', margin: '20px 0 14px' }}
        >
          <span className="text-brand">
            {t('coreloop.challengeTitleSolid')}
          </span>
          <span className={s.outR}>{t('coreloop.challengeTitleOutline')}</span>
        </h3>
        <p className={s.lead} style={{ fontSize: 17, maxWidth: 420 }}>
          {t('coreloop.challengeLead')}
        </p>
        <div
          style={{
            display: 'flex',
            flexDirection: 'column',
            gap: 10,
            marginTop: 16,
          }}
        >
          <div className={s.tickrow}>
            <span className={s.tick}>✓</span>
            {t('coreloop.challengeTick1')}
          </div>
          <div className={s.tickrow}>
            <span className={s.tick}>✓</span>
            {t('coreloop.challengeTick2')}
          </div>
          <div className={s.tickrow}>
            <span className={s.tick}>▶</span>
            {t('coreloop.challengeTick3')}
          </div>
          <div className={s.tickrow}>
            <span className={s.tick}>✕</span>
            {t('coreloop.challengeTick4')}
          </div>
        </div>
      </div>
    </section>
  )
}

/**
 * Gancho comercial: seu jogo vale dinheiro. Valores dos chips são
 * ilustrativos, exibidos na moeda do locale (R$, $, ¥, …) — base em BRL.
 */
export function Pricing() {
  const { t } = useTranslation('landing')
  const { locale } = useRouter()

  return (
    <section id="preco" style={{ textAlign: 'center', padding: '120px 7vw' }}>
      <p
        className={`${s.kick} text-brand`}
        style={{ justifyContent: 'center' }}
      >
        <span className={s.kbar} />
        {t('pricing.kicker')}
        <span className={s.kbar} />
      </p>
      <h2
        className={s.d}
        style={{ fontSize: 'clamp(52px,8vw,132px)', margin: '24px 0 0' }}
      >
        <span className="text-brand">{t('pricing.title1')}</span>
        <br />
        <span className={s.outR}>{t('pricing.title2')}</span>
      </h2>
      <p className={s.lead} style={{ margin: '26px auto 0', maxWidth: 560 }}>
        {t('pricing.lead')}
      </p>
      <div
        style={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          gap: 12,
          flexWrap: 'wrap',
          marginTop: 38,
        }}
      >
        <span className={s.pricechip}>
          {t('pricing.chip1', { price: formatLocalPrice(120, locale) })}
        </span>
        <span className={s.pricechip}>
          {t('pricing.chip2', { price: formatLocalPrice(250, locale) })}
        </span>
        <span className={`${s.pricechip} ${s.pricefill}`}>
          {t('pricing.chip3')}
        </span>
      </div>
      <a
        className={`${s.btn} ${s.btnR}`}
        href="#baixar"
        style={{ marginTop: 38 }}
      >
        {t('pricing.cta')}
      </a>
    </section>
  )
}
